/**
 * THE BUSINESS-FEATURES SECTION — backlog items 34 / 35 / 36.
 *
 * The owner wants a claims list "largely derived from what the business
 * already declares" and "nicer than dk-store's". The derivation happens on the
 * server: `/shop-features` answers ONE list in which six claims are `DERIVED`
 * (free delivery above the owner's own threshold, the card/wallet methods the
 * business actually accepts, the brands it is the official importer of…) and
 * seven are `MANUAL`, dk-store's own set taken as predefined defaults.
 *
 * ── ONE RENDERER, NOT TWO ────────────────────────────────────────────────
 *
 * The single most losable property of this block: `source` changes where a
 * claim's TEXT comes from and nothing else. A derived claim and a manual one
 * are the same object on the page — same card, same glyph disc, same two
 * lines. Two rendering paths would drift, and the owner would see "free
 * delivery" styled unlike "easy returns" for a reason that is invisible to
 * them. So this module flattens both into one row shape and the component
 * maps over it without ever reading `source` again.
 *
 * ── WHERE EACH STRING COMES FROM, IN ORDER ───────────────────────────────
 *
 * 1. The owner's own words (`text`), when they typed any. Either a plain
 *    string or a locale map — the API sends `{ar, en, he}` for a manual claim
 *    the owner has translated, and a bare string is accepted too so an older
 *    payload still renders.
 * 2. `t(labelKey, { defaultValue: defaultLabel })` — our translation, with the
 *    server's own English as the default so a key we have not translated yet
 *    still reads as a sentence rather than as `shop.features.easyReturns`.
 *
 * The SUBLABEL is where the derived facts land, and each one is built from
 * `data` rather than from prose: the threshold with its currency symbol, the
 * payment methods the business accepts, the brands it imports. A claim whose
 * `data` is empty falls back to `${labelKey}Sub` — so nothing renders a
 * half-built sentence.
 *
 * ── OFF BY DEFAULT (item 36) ─────────────────────────────────────────────
 *
 * `enabled` is the owner's switch and it is false until they add the section
 * from My Shop. This module returns an empty list when it is not true, so
 * "the owner has not turned it on" and "there is nothing to show" are the same
 * expression at the call site and the section cannot half-appear.
 */

/** The key a feature's glyph is chosen by. Kept as a plain string so this
 *  module stays free of JSX and can be tested without a renderer. */
export const FEATURE_GLYPHS = {
  OFFICIAL_IMPORTER: 'badge',
  EXCLUSIVE_PRODUCTS: 'star',
  PREMIUM_PACKAGING: 'package',
  EASY_RETURNS: 'refresh',
  GOOD_PRICES: 'tag',
  FREE_DELIVERY: 'truck',
  SECURE_PAYMENT: 'shield',
};
export const DEFAULT_FEATURE_GLYPH = 'check';

/** The payment methods the server can name, and the key each is written with. */
export const PAYMENT_METHOD_LABELS = {
  CREDIT_CARD: { key: 'shop.features.methodCard', defaultValue: 'Credit card' },
  APPLE_PAY: { key: 'shop.features.methodApplePay', defaultValue: 'Apple Pay' },
  GOOGLE_PAY: { key: 'shop.features.methodGooglePay', defaultValue: 'Google Pay' },
  BIT: { key: 'shop.features.methodBit', defaultValue: 'Bit' },
  CASH: { key: 'shop.features.methodCash', defaultValue: 'Cash' },
};

const lang2 = (language) => String(language || 'en').toLowerCase().split('-')[0];

/** The owner's own text for a row, in the reader's language, or ''. */
export function ownerText(value, language) {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value !== 'object') return '';
  const l = lang2(language);
  const direct = value[l];
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  // The `{ ar: { message } }` shape some rows use, accepted so one helper
  // covers both without the caller knowing which it got.
  if (direct && typeof direct.text === 'string' && direct.text.trim()) return direct.text.trim();
  return '';
}

/**
 * The rows to render. `deps` carries only things this module cannot know:
 * the translator, the reader's language, and how to write money in this shop's
 * currency (`formatMoney`), so no currency arithmetic is duplicated here.
 */
export function featureRows(payload, {
  t, language, formatMoney, joinNames,
} = {}) {
  if (!payload || payload.enabled !== true) return [];
  const tr = typeof t === 'function' ? t : ((k, o) => (o && o.defaultValue) || k);
  const rows = Array.isArray(payload.items) ? payload.items : [];
  const join = typeof joinNames === 'function'
    ? joinNames
    : ((names) => names.filter(Boolean).join(', '));

  return rows
    .filter((row) => row && row.isHidden !== true)
    .map((row, index) => {
      const key = String(row.key || '').toUpperCase();
      const labelKey = row.labelKey || `shop.features.${key.toLowerCase()}`;
      const data = row.data && typeof row.data === 'object' ? row.data : {};

      const label = ownerText(row.text, language)
        || tr(labelKey, { defaultValue: row.defaultLabel || key });

      let sublabel = '';
      if (key === 'FREE_DELIVERY' && data.always === true) {
        // Free on every order: there is no amount, so none is quoted.
        sublabel = tr('shop.features.freeDeliveryAlwaysSub', { defaultValue: 'On every order' });
      } else if (key === 'FREE_DELIVERY' && Number(data.threshold) > 0) {
        sublabel = tr('shop.features.freeDeliverySub', {
          defaultValue: 'On orders over {{amount}}',
          amount: typeof formatMoney === 'function'
            ? formatMoney(Number(data.threshold), data.currency)
            : String(data.threshold),
        });
      } else if (key === 'OFFICIAL_IMPORTER' && Array.isArray(data.brands) && data.brands.length) {
        const names = join(data.brands, language);
        if (names) {
          sublabel = tr('shop.features.officialImporterSub', {
            defaultValue: 'Official importer of {{brands}}',
            brands: names,
          });
        }
      } else if (key === 'SECURE_PAYMENT' && Array.isArray(data.methods) && data.methods.length) {
        const named = data.methods
          .map((m) => PAYMENT_METHOD_LABELS[String(m).toUpperCase()])
          .filter(Boolean)
          .map((m) => tr(m.key, { defaultValue: m.defaultValue }));
        sublabel = named.filter(Boolean).join(' · ');
      }
      if (!sublabel) {
        sublabel = tr(`${labelKey}Sub`, { defaultValue: row.defaultSublabel || '' });
      }

      return {
        id: row.id ?? `${key}-${index}`,
        key,
        // Carried for analytics and for the guard that asserts one renderer;
        // NOTHING downstream is allowed to branch a style on it.
        source: row.source || 'MANUAL',
        order: Number.isFinite(Number(row.displayOrder)) ? Number(row.displayOrder) : index,
        index,
        glyph: FEATURE_GLYPHS[key] || DEFAULT_FEATURE_GLYPH,
        label,
        sublabel: sublabel || '',
      };
    })
    .filter((row) => row.label)
    .sort((a, b) => (a.order - b.order) || (a.index - b.index));
}
